import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useGetAnaliseProvaTecnicaDashboardQuery } from "shared/features/api/dashboard/dashboardProvaTecnicaSlice";
import {
  AnaliseProvaTecnicaRegistro,
  useGetAnaliseProvaTecnicaQuery,
} from "shared/features/api/analiseProvaTecnica/analiseProvaTecnicaSlice";

const NOTA_CORTE = 30;
const TAMANHO_BASE_OUTLIERS = 2000;

const colors = {
  teal: "#087f75",
  coral: "#d85b47",
  mustard: "#d69e2e",
  blue: "#315f83",
  line: "#DCE3EA",
  ink: "#172B4D",
  muted: "#5F6F82",
  riskLow: "#286043",
  riskMedium: "#81580b",
  riskHigh: "#8e2518",
  neutral: "rgba(49,95,131,.45)",
};

const seriesOutliers = {
  SEM_ALERTA: { label: "Sem alerta", color: colors.neutral },
  BAIXO: { label: "Risco baixo", color: colors.riskLow },
  MEDIO: { label: "Risco médio", color: colors.riskMedium },
  ALTO: { label: "Risco alto", color: colors.riskHigh },
};

type NivelOutlier = keyof typeof seriesOutliers;

const parseTempo = (value: string) => {
  const partes = (value ?? "").split(":").map(Number);

  return partes.length === 3 && partes.every(Number.isFinite)
    ? partes[0] * 3600 + partes[1] * 60 + partes[2]
    : null;
};

const percentil = (valores: number[], razao: number) => {
  if (!valores.length) return 0;

  const ordenados = [...valores].sort((a, b) => a - b);
  const posicao = (ordenados.length - 1) * razao;
  const base = Math.floor(posicao);
  const resto = posicao - base;

  return ordenados[base + 1] !== undefined
    ? ordenados[base] + resto * (ordenados[base + 1] - ordenados[base])
    : ordenados[base];
};

const formatNumber = (value: number) =>
  value.toLocaleString("pt-BR", { maximumFractionDigits: 2 });

const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds)) return "Não informado";

  return [
    Math.floor(seconds / 3600),
    Math.floor((seconds % 3600) / 60),
    Math.floor(seconds % 60),
  ]
    .map((value) => String(value).padStart(2, "0"))
    .join(":");
};

const formatPercent = (value: number, total: number) =>
  total > 0
    ? (value / total).toLocaleString("pt-BR", {
        style: "percent",
        maximumFractionDigits: 1,
      })
    : "0%";

const formatLabel = (value: string) => {
  const normalized = value.toLocaleLowerCase("pt-BR");

  return normalized.charAt(0).toLocaleUpperCase("pt-BR") + normalized.slice(1);
};

interface ChartCardProps {
  title: string;
  note?: string;
  action?: React.ReactNode;
  height?: number;
  children: React.ReactNode;
}

const ChartCard = ({ title, note, action, height = 320, children }: ChartCardProps) => (
  <Card variant="outlined" sx={{ borderColor: colors.line, borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ color: colors.ink, fontWeight: 700 }}>
            {title}
          </Typography>
          {note && (
            <Typography variant="caption" color={colors.muted} display="block">
              {note}
            </Typography>
          )}
        </Box>
        {action}
      </Box>
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </CardContent>
  </Card>
);

export const DashboardProvaTecnica: React.FC = () => {
  const [periodoOutliers, setPeriodoOutliers] = useState("");
  const { data, isLoading, isError } = useGetAnaliseProvaTecnicaDashboardQuery();
  const {
    data: listagem,
    isLoading: isLoadingListagem,
    isError: isErrorListagem,
  } = useGetAnaliseProvaTecnicaQuery({
    pagina: 0,
    tamanho: TAMANHO_BASE_OUTLIERS,
  });

  const outliers = useMemo(() => {
    const registros = (listagem?.elementos ?? []).map(
      (registro: AnaliseProvaTecnicaRegistro) => ({
        nome: registro.candidatoNome,
        periodo: registro.candidatoSemestreAtual,
        nota: registro.provaNota,
        tempo: parseTempo(registro.provaTempoGasto),
      })
    );
    const comTempo = registros.filter(
      (registro) => registro.tempo !== null && Number.isFinite(registro.nota)
    );
    const notaTop10 = percentil(comTempo.map((registro) => registro.nota), 0.9);
    const notaTop20 = percentil(comTempo.map((registro) => registro.nota), 0.8);
    const temposAprovados = comTempo
      .filter((registro) => registro.nota >= NOTA_CORTE)
      .map((registro) => registro.tempo as number);
    const tempoTop10 = percentil(temposAprovados, 0.1);
    const tempoTop20 = percentil(temposAprovados, 0.2);

    return comTempo.map((registro) => {
      const tempo = registro.tempo as number;
      const rapidoTop10 = tempo <= tempoTop10;
      const rapidoTop20 = tempo <= tempoTop20;
      let nivel: NivelOutlier = "SEM_ALERTA";

      if (registro.nota >= notaTop10 && (rapidoTop10 || registro.periodo === 1)) {
        nivel = "ALTO";
      } else if (registro.nota >= notaTop20 && (rapidoTop20 || registro.periodo <= 2)) {
        nivel = "MEDIO";
      } else if (
        (registro.nota >= notaTop20 && registro.periodo === 3) ||
        (registro.nota >= NOTA_CORTE && rapidoTop10)
      ) {
        nivel = "BAIXO";
      }

      return {
        nome: registro.nome,
        periodo: registro.periodo,
        nota: registro.nota,
        tempoMinutos: Number((tempo / 60).toFixed(1)),
        nivel,
      };
    });
  }, [listagem]);

  const periodosOutliers = Array.from(
    new Set(outliers.map((item) => item.periodo))
  ).sort((a, b) => a - b);
  const outliersFiltrados = outliers.filter(
    (item) => !periodoOutliers || String(item.periodo) === periodoOutliers
  );
  const ordemRisco: NivelOutlier[] = ["ALTO", "MEDIO", "BAIXO", "SEM_ALERTA"];
  const casosRevisao = outliersFiltrados
    .filter((item) => item.nivel !== "SEM_ALERTA")
    .sort(
      (a, b) =>
        ordemRisco.indexOf(a.nivel) - ordemRisco.indexOf(b.nivel) ||
        b.nota - a.nota ||
        a.tempoMinutos - b.tempoMinutos
    );

  const periodos = (data?.periodos ?? []).map((item) => ({
    ...item,
    periodoLabel: `${item.periodo}º`,
  }));
  const aprovadosPorUf = data?.aprovadosPorUf ?? [];
  const topCidades = (data?.topCidadesAprovados ?? []).map((item) => ({
    ...item,
    nome: `${formatLabel(item.cidade)} · ${item.uf}`,
  }));

  const enviadas = data?.quantidadeProvasEnviadas ?? 0;
  const realizadas = data?.quantidadeProvasRealizadas ?? 0;
  const indicadores = [
    {
      label: "Provas enviadas",
      value: formatNumber(enviadas),
      detail: "Total informado",
      color: colors.teal,
    },
    {
      label: "Provas realizadas",
      value: formatNumber(realizadas),
      detail: `${formatPercent(realizadas, enviadas)} das enviadas`,
      color: colors.blue,
    },
    {
      label: "Aprovados",
      value: formatNumber(data?.quantidadeAprovados ?? 0),
      detail: `${formatPercent(data?.quantidadeAprovados ?? 0, realizadas)} das realizadas`,
      color: "#3f8e62",
    },
    {
      label: "Reprovados",
      value: formatNumber(data?.quantidadeReprovados ?? 0),
      detail: `${formatPercent(data?.quantidadeReprovados ?? 0, realizadas)} das realizadas`,
      color: colors.coral,
    },
    {
      label: "Não realizadas",
      value: formatNumber(data?.quantidadeProvasNaoRealizadas ?? 0),
      detail: "Total geral",
      color: colors.mustard,
    },
    {
      label: "Nota média",
      value: formatNumber(data?.notaMedia ?? 0),
      detail: "Todas as provas realizadas",
      color: colors.blue,
    },
    {
      label: "Nota média dos aprovados",
      value: formatNumber(data?.notaMediaAprovados ?? 0),
      detail: "Nota maior ou igual a 30",
      color: colors.teal,
    },
    {
      label: "Tempo médio de prova",
      value: formatTime(data?.tempoMedioSegundos ?? 0),
      detail: "Considerando tempos registrados",
      color: colors.mustard,
    },
  ];

  return (
    <Box>
      {(isLoading || isLoadingListagem) && <LinearProgress sx={{ mb: 2 }} />}
      {(isError || isErrorListagem) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Não foi possível carregar os dados da prova técnica.
        </Alert>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(4, minmax(0, 1fr))",
          },
          gap: 2,
          mb: 2,
        }}
      >
        {indicadores.map((indicador) => (
          <Paper
            key={indicador.label}
            variant="outlined"
            sx={{
              p: 2,
              borderColor: colors.line,
              borderRadius: 3,
              borderTop: `3px solid ${indicador.color}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: colors.muted, fontWeight: 700, textTransform: "uppercase" }}
            >
              {indicador.label}
            </Typography>
            <Typography variant="h4" fontWeight={700} color={colors.ink}>
              {indicador.value}
            </Typography>
            <Typography variant="caption" color={colors.muted} display="block">
              {indicador.detail}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 2,
        }}
      >
        <ChartCard
          title="Média por período"
          note="Comparativo geral e somente aprovados (nota ≥ 30)"
        >
          <ResponsiveContainer>
            <BarChart data={periodos} margin={{ top: 24, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={colors.line} vertical={false} />
              <XAxis dataKey="periodoLabel" tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis tick={{ fill: colors.muted, fontSize: 12 }} />
              <Tooltip formatter={(value: number) => formatNumber(value)} />
              <Legend />
              <Bar dataKey="notaMedia" name="Todos" fill={colors.blue} radius={[6, 6, 0, 0]} />
              <Bar dataKey="notaMediaAprovados" name="Aprovados" fill={colors.teal} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Tempo médio por período"
          note="Comparativo geral, aprovados e reprovados, considerando tempos registrados"
        >
          <ResponsiveContainer>
            <BarChart data={periodos} margin={{ top: 24, right: 12, left: -10, bottom: 0 }}>
              <CartesianGrid stroke={colors.line} vertical={false} />
              <XAxis dataKey="periodoLabel" tick={{ fill: colors.muted, fontSize: 12 }} />
              <YAxis
                tick={{ fill: colors.muted, fontSize: 12 }}
                tickFormatter={(value: number) => `${Math.round(value / 60)} min`}
              />
              <Tooltip formatter={(value: number) => formatTime(value)} />
              <Legend />
              <Bar dataKey="tempoMedioSegundos" name="Todos" fill={colors.mustard} radius={[6, 6, 0, 0]} />
              <Bar dataKey="tempoMedioAprovadosSegundos" name="Aprovados" fill={colors.teal} radius={[6, 6, 0, 0]} />
              <Bar dataKey="tempoMedioReprovadosSegundos" name="Reprovados" fill={colors.coral} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Aprovados por UF"
          note="Quantidade com nota maior ou igual a 30"
          height={Math.max(420, aprovadosPorUf.length * 28)}
        >
          <ResponsiveContainer>
            <BarChart data={aprovadosPorUf} layout="vertical" margin={{ top: 8, right: 35, left: 10, bottom: 0 }}>
              <CartesianGrid stroke={colors.line} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: colors.muted }} />
              <YAxis
                type="category"
                dataKey="uf"
                width={50}
                interval={0}
                tick={{ fill: colors.muted, fontSize: 12 }}
              />
              <Tooltip formatter={(value: number) => [formatNumber(value), "Aprovados"]} />
              <Bar dataKey="quantidadeAprovados" fill={colors.teal} radius={[0, 6, 6, 0]}>
                <LabelList dataKey="quantidadeAprovados" position="right" fill={colors.ink} fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Top 15 cidades com mais aprovados"
          note="Cidade e UF · quantidade de candidatos"
          height={Math.max(420, topCidades.length * 28)}
        >
          <ResponsiveContainer>
            <BarChart data={topCidades} layout="vertical" margin={{ top: 8, right: 35, left: 45, bottom: 0 }}>
              <CartesianGrid stroke={colors.line} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fill: colors.muted }} />
              <YAxis
                type="category"
                dataKey="nome"
                width={170}
                interval={0}
                tick={{ fill: colors.muted, fontSize: 11 }}
              />
              <Tooltip formatter={(value: number) => [formatNumber(value), "Aprovados"]} />
              <Bar dataKey="quantidadeAprovados" fill={colors.blue} radius={[0, 6, 6, 0]}>
                <LabelList dataKey="quantidadeAprovados" position="right" fill={colors.ink} fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>

      <Box sx={{ mt: 2 }}>
        <ChartCard
          title="Desempenho atípico: nota × tempo de prova"
          note="Cada ponto é um candidato com tempo registrado. Notas altas obtidas em tempos curtos ou em períodos iniciais são sinalizadas por nível de risco para revisão humana; o alerta não comprova irregularidade."
          height={420}
          action={
            <TextField
              select
              size="small"
              label="Período do gráfico"
              value={periodoOutliers}
              onChange={(event) => setPeriodoOutliers(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">Todos</MenuItem>
              {periodosOutliers.map((periodo) => (
                <MenuItem key={periodo} value={String(periodo)}>
                  {periodo}º período
                </MenuItem>
              ))}
            </TextField>
          }
        >
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 16, right: 24, left: 0, bottom: 16 }}>
              <CartesianGrid stroke={colors.line} />
              <XAxis
                type="number"
                dataKey="tempoMinutos"
                name="Tempo"
                unit=" min"
                tick={{ fill: colors.muted, fontSize: 12 }}
              />
              <YAxis
                type="number"
                dataKey="nota"
                name="Nota"
                domain={[0, 100]}
                tick={{ fill: colors.muted, fontSize: 12 }}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={({ payload }) => {
                  const ponto = payload?.[0]?.payload as
                    | {
                        nome: string;
                        periodo: number;
                        nota: number;
                        tempoMinutos: number;
                        nivel: NivelOutlier;
                      }
                    | undefined;

                  if (!ponto) return null;

                  return (
                    <Paper variant="outlined" sx={{ p: 1.5, borderColor: colors.line }}>
                      <Typography variant="subtitle2" fontWeight={700} color={colors.ink}>
                        {ponto.nome}
                      </Typography>
                      <Typography variant="caption" color={colors.muted} display="block">
                        {ponto.periodo}º período · nota {formatNumber(ponto.nota)}
                      </Typography>
                      <Typography variant="caption" color={colors.muted} display="block">
                        Tempo: {formatTime(ponto.tempoMinutos * 60)}
                      </Typography>
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ color: seriesOutliers[ponto.nivel].color, fontWeight: 700 }}
                      >
                        {seriesOutliers[ponto.nivel].label}
                      </Typography>
                    </Paper>
                  );
                }}
              />
              <Legend />
              {(Object.keys(seriesOutliers) as NivelOutlier[]).map((nivel) => (
                <Scatter
                  key={nivel}
                  name={seriesOutliers[nivel].label}
                  fill={seriesOutliers[nivel].color}
                  data={outliersFiltrados.filter((item) => item.nivel === nivel)}
                />
              ))}
            </ScatterChart>
          </ResponsiveContainer>
        </ChartCard>
      </Box>

      <Card variant="outlined" sx={{ borderColor: colors.line, borderRadius: 3, mt: 2 }}>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ color: colors.ink, fontWeight: 700 }}>
              Casos para revisão
            </Typography>
            <Typography variant="caption" color={colors.muted}>
              {casosRevisao.length} candidato(s) na seleção atual
            </Typography>
          </Box>

          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ borderColor: colors.line, maxHeight: 520 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {["Risco", "Candidato", "Período", "Nota", "Tempo"].map((header, index) => (
                    <TableCell
                      key={header}
                      align={index > 2 ? "right" : "left"}
                      sx={{ color: colors.muted, fontWeight: 700, bgcolor: "#F4F7FA" }}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {casosRevisao.map((caso) => (
                  <TableRow key={`${caso.nome}-${caso.nota}-${caso.tempoMinutos}`} hover>
                    <TableCell>
                      <Chip
                        size="small"
                        label={seriesOutliers[caso.nivel].label}
                        sx={{
                          color: seriesOutliers[caso.nivel].color,
                          bgcolor: `${seriesOutliers[caso.nivel].color}1A`,
                          fontWeight: 700,
                        }}
                      />
                    </TableCell>
                    <TableCell>{caso.nome}</TableCell>
                    <TableCell>{caso.periodo}º</TableCell>
                    <TableCell align="right">{formatNumber(caso.nota)}</TableCell>
                    <TableCell align="right">{formatTime(caso.tempoMinutos * 60)}</TableCell>
                  </TableRow>
                ))}
                {!casosRevisao.length && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: colors.muted }}>
                      Nenhum caso atende aos critérios na seleção atual.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};
